import { DialogComponent } from '@theme/dialog';

/**
 * Custom element that powers the "Info drawer tabs" block.
 *
 * Extends DialogComponent so it inherits showDialog/closeDialog and the
 * declarative `on:click="/showDialog"` etc. bindings used by the theme.
 *
 * Adds:
 *   - openToTab(event): opens the drawer and selects the tab named by
 *     the trigger's `data-tab-target` attribute.
 *   - selectTab(event): switches tabs while the drawer is open, using
 *     the tab button's `data-tab-id` attribute.
 *
 * @extends DialogComponent
 */
export class InfoDrawerTabsComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.#placePanels();
    requestAnimationFrame(() => {
      this.#placePanels();
      // Sync trigger row labels + tab strip labels from each panel's
      // data-tab-label. The panels read their own block settings, which
      // is more reliable than the parent template iterating block.blocks
      // and reading nested settings (which can come back blank).
      this.#syncLabels();

      // Select the first tab by default so something is visible when the
      // drawer opens, even if no specific trigger has been clicked yet.
      this.#ensureActiveTab();
    });
  }

  /**
   * St. Luca: child blocks render in the trigger row. Move each drawer tab
   * panel into the drawer and put its trigger where the panel was, so
   * regular blocks and drawer rows keep the order set in the editor.
   */
  #placePanels() {
    const row = this.querySelector('.info-drawer-tabs-component__triggers');
    const drawer = this.querySelector('.info-drawer-tabs-component__panels');
    if (!row || !drawer) return;
    for (const panel of Array.from(row.querySelectorAll(':scope > [data-tab-panel]'))) {
      const id = panel.getAttribute('data-tab-panel');
      const trigger = id ? row.querySelector(`:scope > [data-tab-target="${CSS.escape(id)}"]`) : null;
      if (trigger) panel.before(trigger);
      drawer.append(panel);
    }
    // Triggers / tabs rendered for regular (non drawer tab) blocks have no
    // matching panel: remove them.
    const ids = new Set(this.#panels().map((p) => p.getAttribute('data-tab-panel')));
    for (const el of Array.from(this.querySelectorAll('[data-tab-target]'))) {
      if (!ids.has(el.getAttribute('data-tab-target'))) el.remove();
    }
    // The drawer's tab strip loops over the blocks a second time, and Shopify
    // suffixes repeated block ids ("…-1"), so map them back to the panel id.
    for (const el of Array.from(this.querySelectorAll('[data-tab-id]'))) {
      const raw = el.getAttribute('data-tab-id') || '';
      const id = ids.has(raw) ? raw : raw.replace(/-\d+$/, '');
      if (!ids.has(id)) { el.remove(); continue; }
      el.setAttribute('data-tab-id', id);
      el.setAttribute('aria-controls', `info-drawer-panel-${id}`);
    }
    this.setAttribute('data-idt-ready', '');
  }

  /**
   * Copies each panel's data-tab-label into its matching trigger row and
   * tab strip button.
   */
  #syncLabels() {
    for (const panel of this.#panels()) {
      const id = panel.getAttribute('data-tab-panel');
      const label = panel.getAttribute('data-tab-label');
      if (!id || !label) continue;

      const trigger = this.querySelector(`[data-tab-target="${CSS.escape(id)}"] .info-drawer-tabs-component__trigger-label`);
      if (trigger) trigger.textContent = label;

      const tab = this.querySelector(`[data-tab-id="${CSS.escape(id)}"]`);
      if (tab) tab.textContent = label;
    }
  }

  /**
   * Triggered by a row on the product page. Opens the drawer and selects
   * the tab whose id matches `data-tab-target` on the clicked button.
   *
   * @param {Event} event
   */
  openToTab(event) {
    // The theme framework delegates clicks at the document level and proxies
    // `event.target` to point at the element bound with `on:click`. We must
    // use `event.target`, NOT `event.currentTarget` (which is `document`).
    const trigger = event.target instanceof Element
      ? event.target.closest('[data-tab-target]')
      : null;
    const target = trigger?.getAttribute('data-tab-target');

    if (target) {
      this.#activate(target);
    } else {
      this.#ensureActiveTab();
    }

    this.showDialog();
  }

  /**
   * Triggered by a tab button inside the drawer's tablist. Switches the
   * active panel without closing the drawer.
   *
   * @param {Event} event
   */
  selectTab(event) {
    const button = event.target instanceof Element
      ? event.target.closest('[data-tab-id]')
      : null;
    const target = button?.getAttribute('data-tab-id');
    if (target) this.#activate(target);
  }

  /**
   * Ensures at least one tab is marked active. Picks the first available
   * tab if none is currently active.
   */
  #ensureActiveTab() {
    const tabs = this.#tabs();
    if (tabs.length === 0) return;
    const alreadyActive = tabs.some((tab) => tab.getAttribute('aria-selected') === 'true');
    if (!alreadyActive) this.#activate(tabs[0].getAttribute('data-tab-id'));
  }

  /**
   * Marks the tab + panel with the given id as active, and updates ARIA
   * state on all the others.
   *
   * @param {string | null | undefined} id
   */
  #activate(id) {
    if (!id) return;

    for (const tab of this.#tabs()) {
      const match = tab.getAttribute('data-tab-id') === id;
      tab.setAttribute('aria-selected', match ? 'true' : 'false');
      tab.tabIndex = match ? 0 : -1;
    }

    for (const panel of this.#panels()) {
      const match = panel.getAttribute('data-tab-panel') === id;
      panel.setAttribute('data-active', match ? 'true' : 'false');
      panel.toggleAttribute('hidden', !match);
    }
  }

  /**
   * @returns {HTMLElement[]}
   */
  #tabs() {
    return Array.from(this.querySelectorAll('[data-tab-id]'));
  }

  /**
   * @returns {HTMLElement[]}
   */
  #panels() {
    return Array.from(this.querySelectorAll('[data-tab-panel]'));
  }
}

if (!customElements.get('info-drawer-tabs-component')) {
  customElements.define('info-drawer-tabs-component', InfoDrawerTabsComponent);
}
